/**
 * Relay push governor — owns the single SSE connection to GET /api/stream
 * and fans frames out to topic subscribers (accountStore, useHeadroom,
 * useRateLimitStatus, useUserKalshiPOS). The polling loops those consumers
 * already have stay intact as the FALLBACK: each consumer asks
 * `topicActive(topic)` before arming its timer and re-arms it whenever push
 * drops (`onActiveChange`). Mixed-version safe: an old relay 404s /api/stream
 * and everything polls exactly as today.
 *
 * Transport: SSE over fetch-streaming (Authorization header works; Apache
 * mod_rewrite [P] on the prod lanes carries it as a plain chunked response —
 * WebSocket upgrades would not traverse it). Recovery is reconnect +
 * fresh snapshots from the server; no replay. Stall detector: no bytes
 * (heartbeats count) for 40s → reconnect.
 *
 * Topic availability: the server sends {__unavailable: true} for topics it
 * can't stream for this session (e.g. orders/pos for personal-key scope) —
 * those consumers keep polling while the rest ride the stream.
 */
import { apiFetch, useAuthStore } from '../stores/authStore';
import { createSseParser } from '../lib/streamParser';
import { probePushApply } from '../lib/trading/feedLatencyProbe';
import { createRelayClock, elapsedSince, localClockStamp, type LocalClockStamp, type RelayClock } from '../lib/relayClock';

export type PushTopic =
  | 'positions'
  | 'pnl'
  | 'orders'
  | 'pos'
  | 'fills'
  | 'balance'
  | 'headroom'
  | 'rateLimit'
  // RFQ lane (relay/src/push/push.ts must list the same names). Lane-org +
  // rfq-grant scoped server-side; a session outside the lane gets
  // {__unavailable:true} and the RFQ stores keep polling.
  | 'rfq.health'
  | 'rfq.board'
  | 'rfq.decisions'
  | 'rfq.quotes'
  | 'rfq.firehose'
  | 'rfq.proposals';

const ALL_TOPICS: PushTopic[] = [
  'positions',
  'pnl',
  'orders',
  'pos',
  'fills',
  'balance',
  'headroom',
  'rateLimit',
  'rfq.health',
  'rfq.board',
  'rfq.decisions',
  'rfq.quotes',
  'rfq.firehose',
  'rfq.proposals',
];

function authScopeKey(): string {
  const user = useAuthStore.getState().user;
  return JSON.stringify([user?.id, user?.orgId, user?.role]);
}

const STALL_MS = 40_000;
// The org poller republishes orders/pos at least every 10s while healthy,
// including unchanged books. Three missed publications restore read polling;
// transport heartbeats and unrelated topics cannot extend this deadline.
const SNAPSHOT_STALL_MS = 30_000;
const SNAPSHOT_TOPICS: readonly PushTopic[] = ['orders', 'pos'];
const UNAVAILABLE_RETRY_MS = 5 * 60_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

const pushEnabled =
  import.meta.env.VITE_RELAY_PUSH === '1' || import.meta.env.VITE_RELAY_PUSH === 'true';

export interface PushFrame {
  topic: PushTopic;
  /** Relay emit stamp (epoch ms) — the `__pushLatency()` probe's wire mark. */
  ts: number;
  payload: unknown;
}

type TopicCb = (payload: unknown, clock?: RelayClock | null) => void;

const topicSubs = new Map<PushTopic, Set<TopicCb>>();
const activeChangeCbs = new Set<() => void>();
const unavailableTopics = new Set<PushTopic>();
const staleTopics = new Set<PushTopic>();
const snapshotReceivedAt = new Map<PushTopic, LocalClockStamp>();

let streamActive = false;
let running = false;
let runGeneration = 0;
let abort: AbortController | null = null;
let backoffMs = BACKOFF_BASE_MS;
let unavailableSince: LocalClockStamp | null = null;
let stallTimer: ReturnType<typeof setInterval> | null = null;

function setStreamActive(next: boolean): void {
  if (streamActive === next) return;
  streamActive = next;
  staleTopics.clear();
  snapshotReceivedAt.clear();
  if (!next) unavailableTopics.clear();
  else for (const topic of SNAPSHOT_TOPICS) snapshotReceivedAt.set(topic, localClockStamp());
  notifyActiveChange();
}

function notifyActiveChange(): void {
  for (const cb of activeChangeCbs) {
    try {
      cb();
    } catch {
      // subscriber's problem
    }
  }
}

/** True when the stream is up AND the server can serve this topic. Consumers
 *  arm their polling timers when this is false. */
export function topicActive(topic: PushTopic): boolean {
  return streamActive && !unavailableTopics.has(topic) && !staleTopics.has(topic);
}

export function isPushActive(): boolean {
  return streamActive;
}

/** Fires on any stream up/down or topic-availability change — consumers
 *  re-evaluate their polling from it. Returns unsubscribe. */
export function onActiveChange(cb: () => void): () => void {
  activeChangeCbs.add(cb);
  return () => activeChangeCbs.delete(cb);
}

/** Credentials/key mode changed without necessarily rotating the auth token.
 * Abort the old scope immediately; the existing loop reconnects and polling
 * covers the gap. This never opens a second concurrent connection. */
export function invalidateRelayPushSession(): void {
  abort?.abort();
  setStreamActive(false);
}

/** Subscribe to a topic's frames. First subscriber overall starts the
 *  connection; last one stops it. Returns unsubscribe. */
export function subscribePush(topic: PushTopic, cb: TopicCb): () => void {
  let set = topicSubs.get(topic);
  if (!set) topicSubs.set(topic, (set = new Set()));
  set.add(cb);
  ensureRunning();
  return () => {
    set.delete(cb);
    if (set.size === 0) topicSubs.delete(topic);
    if (topicSubs.size === 0) stop();
  };
}

/** Fan one decoded `update` frame out to its topic subscribers. Exported for
 *  the push-latency probe test; the stream loop is the only runtime caller. */
export function dispatchPushFrame(frame: PushFrame, clock: RelayClock | null = null): void {
  // relay `ts` → apply: the envelope stamp used to be dropped here, so the
  // relay→browser leg was unmeasurable. Stamp before the availability
  // bookkeeping so a topic's first frame counts too.
  if (typeof frame.ts === 'number') probePushApply(frame.topic, frame.ts);
  const payload = frame.payload as { __unavailable?: boolean } | null;
  if (payload && typeof payload === 'object' && payload.__unavailable === true) {
    if (!unavailableTopics.has(frame.topic)) {
      unavailableTopics.add(frame.topic);
      notifyActiveChange();
    }
    return;
  }
  // Delivery evidence is separate from physical queue freshness. Never stamp
  // queueStatus or order observations here. Consumers still validate them.
  const snapshotTopic = SNAPSHOT_TOPICS.includes(frame.topic);
  const envelope = payload as { orders?: unknown; positions?: unknown } | null;
  const snapshotEnvelope = envelope && Array.isArray(envelope.orders) &&
    (frame.topic !== 'pos' || Array.isArray(envelope.positions));
  const delivered = !snapshotTopic || !!snapshotEnvelope;
  if (snapshotTopic && delivered) snapshotReceivedAt.set(frame.topic, localClockStamp());
  const wasUnavailable = delivered && unavailableTopics.delete(frame.topic);
  const wasStale = delivered && staleTopics.delete(frame.topic);
  const subs = topicSubs.get(frame.topic);
  for (const cb of subs ?? []) {
    try {
      cb(frame.payload, clock);
    } catch (err) {
      console.error(`[relay-push] ${frame.topic} subscriber threw:`, err);
    }
  }
  if (wasUnavailable || wasStale) notifyActiveChange();
}

function ensureRunning(): void {
  if (!pushEnabled || running) return;
  running = true;
  void connectLoop(++runGeneration);
}

function stop(): void {
  running = false;
  ++runGeneration;
  abort?.abort();
  abort = null;
  if (stallTimer) clearInterval(stallTimer);
  stallTimer = null;
  setStreamActive(false);
}

async function connectLoop(generation: number): Promise<void> {
  const isCurrentRun = () => running && generation === runGeneration;
  while (isCurrentRun()) {
    // Not logged in yet / logged out → wait, don't spin.
    if (!useAuthStore.getState().token) {
      await sleep(2000);
      continue;
    }
    if (unavailableSince && elapsedSince(unavailableSince) < UNAVAILABLE_RETRY_MS) {
      await sleep(5000);
      continue;
    }
    try {
      const controller = new AbortController();
      abort = controller;
      // Polling recovery must not wait for a browser body reader to settle.
      // An old run's delayed abort must not disable its replacement.
      controller.signal.addEventListener('abort', () => {
        if (isCurrentRun() && abort === controller) setStreamActive(false);
      }, { once: true });
      const requestStarted = localClockStamp();
      const sessionToken = useAuthStore.getState().token;
      const sessionScope = _lastScope;
      let relayClock: RelayClock | null = null;
      const res = await apiFetch(`/api/stream?topics=${ALL_TOPICS.join(',')}`, {
        signal: controller.signal,
      });
      if (!isCurrentRun() || controller.signal.aborted) {
        // Cleanup cannot own reconnect liveness; cancellation can reject or
        // remain pending independently of this subscription run.
        void res.body?.cancel().catch(() => {});
        if (!isCurrentRun()) return;
        // Token rotation aborts the request, not the subscription run. An
        // already-fulfilled/bodyless response may still reach this await;
        // do not leave running=true with no loop left to reconnect.
        throw new DOMException('Stream request aborted', 'AbortError');
      }
      if (res.status === 404) {
        // Push disabled on this lane (or old relay) — back off for a while.
        unavailableSince = localClockStamp();
        setStreamActive(false);
        continue;
      }
      if (!res.ok || !res.body) {
        throw new Error(`stream HTTP ${res.status}`);
      }

      unavailableSince = null;
      // Local duration, not an epoch deadline: a backward clock correction
      // must not strand a dead stream. The wall-time bound also detects sleep
      // on browsers whose monotonic clock pauses. A forward jump may cause a
      // conservative read-only reconnect; it never replays trading actions.
      let lastByteAt = localClockStamp();
      stallTimer = setInterval(() => {
        if (!isCurrentRun() || controller.signal.aborted) return;
        let changed = false;
        if (streamActive) for (const topic of SNAPSHOT_TOPICS) {
          const received = snapshotReceivedAt.get(topic);
          if (received && elapsedSince(received) >= SNAPSHOT_STALL_MS && !staleTopics.has(topic)) {
            staleTopics.add(topic);
            changed = true;
          }
        }
        if (changed) notifyActiveChange();
        if (elapsedSince(lastByteAt) > STALL_MS) {
          console.warn('[relay-push] stall — reconnecting');
          controller.abort();
        }
      }, 5000);

      const parser = createSseParser(
        (e) => {
          // Buffered old-user bytes can arrive after abort; never dispatch
          // them into the next account's store or sound session.
          if (!isCurrentRun() || controller.signal.aborted || sessionToken !== useAuthStore.getState().token
            || sessionScope !== _lastScope) return;
          if (e.event === 'bye') {
            // Deploy goodbye — reconnect promptly with jitter so the herd
            // spreads across the new process's first second.
            setTimeout(() => controller.abort(), Math.random() * 3000);
            return;
          }
          if (e.event === 'hello') {
            try { relayClock = createRelayClock(JSON.parse(e.data).serverTs, requestStarted); }
            catch { relayClock = null; }
            backoffMs = BACKOFF_BASE_MS;
            setStreamActive(true);
            return;
          }
          if (e.event === 'update') {
            try {
              dispatchPushFrame(JSON.parse(e.data) as PushFrame, relayClock);
            } catch {
              // malformed frame — drop; next snapshot heals
            }
          }
        },
        () => {
          // heartbeat comment — liveness only
        },
      );

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        lastByteAt = localClockStamp();
        parser(decoder.decode(value, { stream: true }));
      }
    } catch {
      // abort / network — fall through to reconnect
    } finally {
      // stop() already clears this run's timer. A replacement can start
      // before the old aborted reader settles; old cleanup must not own it.
      if (generation === runGeneration) {
        if (stallTimer) clearInterval(stallTimer);
        stallTimer = null;
        setStreamActive(false);
      }
    }
    if (!isCurrentRun()) break;
    const delay = Math.random() * backoffMs;
    backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
    await sleep(delay);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// Token change (login/logout/refresh-rotation) → tear the stream down NOW.
// Without this a logged-out tab's stream would keep delivering the previous
// user's frames and repopulate caches the logout wipe just cleared (the same
// class of leak the hooks' epoch guards exist for).
let _lastScope = authScopeKey();
let _lastToken: string | null = useAuthStore.getState().token ?? null;
useAuthStore.subscribe((s) => {
  const token = s.token ?? null;
  const scope = authScopeKey();
  if (token !== _lastToken || scope !== _lastScope) {
    _lastToken = token;
    _lastScope = scope;
    abort?.abort();
  }
});
